/**
 * SchemaNode — recursive renderer for any JSON node guided by its schema.
 *
 * Renders:
 *   - Object: scalar fields as key-value pairs, arrays/objects as nested nodes
 *   - Array of objects: as SchemaArrayTable
 *   - Primitive: as FieldEditor
 */

import React, { useState, useCallback } from 'react';
import FieldEditor from './FieldEditor';
import SchemaArrayTable from './SchemaArrayTable';
import { categorizeProperties, getFieldLabel, getFieldType, getArrayItemSchema, resolveLabelWithFallback, createDefaultObject, hasNestedChildren, getOverlayType } from '../../utils/schemaUtils';
import { computeBalanceSummary } from './overlays/RunningBalanceOverlay';
import { computeShareCountReconciliation, computePortfolioReconciliation, computeTransactionArithmetic, computeMarketValueConsistency, computeTransactionCashFlowBalance } from './overlays/InvestmentReconciliationOverlay';
import ConfirmBeforeActionDialog from '../ui/dialog-confirm-before-action';
import styles from './SchemaNode.module.css';

// readOnly: a view whose edits never publish (Step 2 statement summary) renders no
// add / delete / edit controls at all -- a control that silently does nothing is
// worse than no control (a reviewer "deleted" two accounts there and nothing happened).
function SchemaNode({ data, schema, path = [], onChange, onInteraction, defaultExpanded = true, rootData, readOnly = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [pendingDelete, setPendingDelete] = useState(null); // { key, idx, label }

  const type = getFieldType(schema);

  // Handle field change at any depth
  const handleFieldChange = useCallback((fieldKey, value) => {
    const newData = { ...data, [fieldKey]: value };
    onChange(newData);
  }, [data, onChange]);

  // Handle array item change
  const handleArrayChange = useCallback((fieldKey, newItems) => {
    const newData = { ...data, [fieldKey]: newItems };
    onChange(newData);
  }, [data, onChange]);

  // Render object type
  if (type === 'object' && data && typeof data === 'object' && !Array.isArray(data)) {
    const { scalars, arrays, objects } = categorizeProperties(schema);

    // Derive a label for this node. resolveLabelWithFallback honors
    // x-label-template-fallback when the primary template would render
    // empty (e.g. an Apple Card account whose account_number_last4 is null).
    const labelTemplate = schema?.['x-label-template'];
    const displayLabel = schema?.['x-display-label'] || '';
    const nodeLabel = labelTemplate
      ? (resolveLabelWithFallback(schema, data) || displayLabel)
      : displayLabel;

    // Compute balance status for this node
    // Direct: this node has a child array with running_balance overlay
    // Aggregate: this node has a child array whose items have running_balance arrays
    // Investment: this node has a child array with investment_reconciliation overlay
    let balanceStatus = null; // null = N/A, true = reconciled, false = discrepancy
    let balanceStatus_overlay = null; // track overlay type for tooltip text
    for (const { key, schema: arrSchema } of arrays) {
      const overlay = getOverlayType(arrSchema);
      if (overlay === 'running_balance') {
        // Direct: this is a statement-level node with transactions
        const summary = computeBalanceSummary(data, data[key] || []);
        balanceStatus = !summary.hasDiscrepancy;
      } else if (overlay === 'investment_reconciliation') {
        // Investment: compute all reconciliation checks.
        // securities and cash_positions are now nested at the statement level
        // (siblings of transactions), so read from data (the statement object).
        const securities = data.securities || [];
        const cashPositions = data.cash_positions || [];
        const shareRecon = computeShareCountReconciliation(data[key] || [], securities);
        const portfolioRecon = computePortfolioReconciliation(data, securities, cashPositions);
        const txnArithmetic = computeTransactionArithmetic(data[key] || []);
        const mvConsistency = computeMarketValueConsistency(securities);
        const txnBalance = computeTransactionCashFlowBalance(data, data[key] || []);
        // Txn Balance is informational for investment statements — exclude from pass/fail
        const allChecks = [shareRecon.passed, portfolioRecon.passed, txnArithmetic.passed, mvConsistency.passed];
        balanceStatus = allChecks.every(c => c !== false);
        balanceStatus_overlay = 'investment_reconciliation';
      } else {
        // Check if child items have running_balance arrays (account -> statements -> transactions)
        const childItemSchema = getArrayItemSchema(arrSchema);
        const childArrays = Object.entries(childItemSchema?.properties || {})
          .filter(([, s]) => getOverlayType(s) === 'running_balance');
        if (childArrays.length > 0) {
          const childItems = data[key] || [];
          const allReconciled = childItems.length > 0 && childItems.every(child => {
            return childArrays.every(([txnKey]) => {
              const summary = computeBalanceSummary(child, child[txnKey] || []);
              return !summary.hasDiscrepancy;
            });
          });
          balanceStatus = childItems.length > 0 ? allReconciled : null;
        }
      }
    }

    return (
      <div className={styles.objectNode}>
        {nodeLabel && (
          <div className={styles.nodeHeader} onClick={() => setExpanded(!expanded)}>
            <span className={styles.expandIcon}>{expanded ? '▾' : '▸'}</span>
            <span className={styles.nodeLabel}>{nodeLabel}</span>
            {balanceStatus !== null && (
              <span
                className={balanceStatus ? styles.statusDotOk : styles.statusDotError}
                title={balanceStatus_overlay === 'investment_reconciliation'
                  ? (balanceStatus === null ? 'Investment reconciliation: no data available'
                    : balanceStatus ? 'Investment reconciliation passed'
                    : 'Investment reconciliation failed')
                  : (balanceStatus ? 'Balances reconciled' : 'Balance discrepancy')}
              />
            )}
          </div>
        )}
        {(expanded || !nodeLabel) && (
          <div className={styles.nodeBody}>
            {/* Scalar fields as key-value grid */}
            {scalars.length > 0 && (
              <div className={styles.fieldGrid}>
                {scalars.map(({ key, schema: fieldSchema, label }) => (
                  <div key={key} className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>{label}</span>
                    <FieldEditor
                      value={data[key]}
                      fieldSchema={fieldSchema}
                      fieldKey={key}
                      readOnly={readOnly}
                      onChange={(val) => handleFieldChange(key, val)}
                      onPageClick={(page) => onInteraction?.({
                        type: 'transaction_selected',
                        data: { ...data, page_number: page },
                      })}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Nested objects */}
            {objects.map(({ key, schema: fieldSchema, label }) => (
              <div key={key} className={styles.nestedSection}>
                <SchemaNode
                  data={data[key] || {}}
                  schema={fieldSchema}
                  path={[...path, key]}
                  onChange={(val) => handleFieldChange(key, val)}
                  onInteraction={onInteraction}
                  rootData={rootData}
                  readOnly={readOnly}
                />
              </div>
            ))}

            {/* Arrays */}
            {arrays.map(({ key, schema: arraySchema, label }) => {
              const itemSchema = getArrayItemSchema(arraySchema);
              const itemType = getFieldType(itemSchema);
              const items = data[key] || [];
              const nested = itemType === 'object' && hasNestedChildren(itemSchema);

              return (
                <div key={key} className={styles.arraySection}>
                  <ArrayHeader
                    label={label}
                    count={items.length}
                    onAdd={readOnly ? null : () => {
                      const newItem = itemType === 'object' ? createDefaultObject(itemSchema) : '';
                      handleArrayChange(key, [...items, newItem]);
                    }}
                  />
                  {nested ? (
                    /* Items have nested arrays/objects — render each as a collapsible SchemaNode */
                    items.map((item, idx) => (
                      <div key={item.id || idx} className={styles.nestedArrayItem}>
                        <SchemaNode
                          data={item}
                          schema={itemSchema}
                          path={[...path, key, idx]}
                          onChange={(newItem) => {
                            const newItems = items.map((it, i) => i === idx ? newItem : it);
                            handleArrayChange(key, newItems);
                          }}
                          onInteraction={onInteraction}
                          rootData={rootData}
                          defaultExpanded={false}
                          readOnly={readOnly}
                        />
                        {!readOnly && (
                          <button
                            className={styles.removeItemBtn}
                            onClick={() => setPendingDelete({ key, idx, label: label.replace(/s$/, '') })}
                            title="Delete"
                            aria-label={`Delete ${label.replace(/s$/, '')}`}
                          >✕</button>
                        )}
                      </div>
                    ))
                  ) : itemType === 'object' ? (
                    /* Leaf-level objects — render as flat table */
                    <SchemaArrayTable
                      items={items}
                      arraySchema={arraySchema}
                      parentData={data}
                      rootData={rootData}
                      onChange={(newItems) => handleArrayChange(key, newItems)}
                      onInteraction={onInteraction}
                      readOnly={readOnly}
                    />
                  ) : (
                    <PrimitiveList
                      items={items}
                      onChange={(newItems) => handleArrayChange(key, newItems)}
                      readOnly={readOnly}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
        <ConfirmBeforeActionDialog
          open={pendingDelete !== null}
          title={`Delete ${pendingDelete?.label || 'Item'}`}
          confirmMessage={`Are you sure you want to delete this ${pendingDelete?.label || 'item'}? This cannot be undone after saving.`}
          proceedButtonLabel="Delete"
          cancelButtonLabel="Cancel"
          onProceed={() => {
            if (pendingDelete) {
              const items = data[pendingDelete.key] || [];
              handleArrayChange(pendingDelete.key, items.filter((_, i) => i !== pendingDelete.idx));
            }
            setPendingDelete(null);
          }}
          onCancel={() => setPendingDelete(null)}
          list_element={() => null}
        />
      </div>
    );
  }

  // Render array at top level (rare — usually arrays are nested in objects)
  if (type === 'array' && Array.isArray(data)) {
    const itemSchema = getArrayItemSchema(schema);
    const itemType = getFieldType(itemSchema);

    return (
      <div className={styles.arrayNode}>
        {itemType === 'object' ? (
          <SchemaArrayTable
            items={data}
            arraySchema={schema}
            parentData={{}}
            rootData={rootData}
            onChange={onChange}
            onInteraction={onInteraction}
            readOnly={readOnly}
          />
        ) : (
          <PrimitiveList items={data} onChange={onChange} readOnly={readOnly} />
        )}
      </div>
    );
  }

  // Fallback: render as plain text
  return <span>{JSON.stringify(data)}</span>;
}

// --- Helper components ---

function ArrayHeader({ label, count, onAdd }) {
  return (
    <div className={styles.arrayHeader}>
      <span className={styles.arrayLabel}>{label}</span>
      <span className={styles.arrayCount}>{count} item(s)</span>
      {onAdd && <button className={styles.addBtn} onClick={onAdd}>+ Add</button>}
    </div>
  );
}

function PrimitiveList({ items, onChange, readOnly = false }) {
  return (
    <div className={styles.primitiveList}>
      {items.map((item, idx) => (
        <div key={idx} className={styles.primitiveItem}>
          <input
            className={styles.primitiveInput}
            value={item ?? ''}
            readOnly={readOnly}
            onChange={(e) => {
              const newItems = [...items];
              newItems[idx] = e.target.value;
              onChange(newItems);
            }}
          />
          {!readOnly && (
            <button
              className={styles.removeBtn}
              onClick={() => onChange(items.filter((_, i) => i !== idx))}
              aria-label="Remove item"
            >✕</button>
          )}
        </div>
      ))}
    </div>
  );
}

export default SchemaNode;
